import { apiFetch } from "@/lib/api/client";
import { apiUrl } from "@/lib/api-config";
import { getJwtToken } from "@/lib/auth-client";
import {
  badgesFromNotifications,
  type ChatProcessNotification,
  type ConversationProcessBadge,
} from "./chat-process-notifications.logic";

export type { ChatProcessNotification, ConversationProcessBadge };
export { badgesFromNotifications };

export async function fetchUnseenChatProcessNotifications(): Promise<ChatProcessNotification[]> {
  const data = await apiFetch<{ notifications?: ChatProcessNotification[] }>(
    "/api/chat/processes/notifications"
  );
  return Array.isArray(data.notifications) ? data.notifications : [];
}

export async function markChatProcessNotificationsSeen(conversationId: string): Promise<void> {
  await apiFetch("/api/chat/processes/notifications/seen", {
    method: "POST",
    body: JSON.stringify({ conversationId }),
  });
}

/**
 * Observe user-scoped process completion notifications via SSE.
 * Aborting only stops listening — it does not cancel any process.
 */
export async function observeChatProcessNotifications(params: {
  signal?: AbortSignal;
  onSnapshot: (notifications: ChatProcessNotification[]) => void;
  onUpdated: (notification: ChatProcessNotification) => void;
}): Promise<void> {
  const token = await getJwtToken();
  const url = apiUrl("/api/chat/processes/notifications/stream");
  const response = await fetch(url, {
    method: "GET",
    credentials: "include",
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    signal: params.signal,
  });
  if (!response.ok || !response.body) {
    throw new Error(`Failed to observe chat process notifications (${response.status})`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const chunks = buffer.split("\n\n");
    buffer = chunks.pop() ?? "";

    for (const chunk of chunks) {
      const lines = chunk.split("\n");
      let eventName = "message";
      let dataLine = "";
      for (const line of lines) {
        if (line.startsWith("event:")) eventName = line.slice(6).trim();
        if (line.startsWith("data:")) dataLine += line.slice(5).trim();
      }
      if (!dataLine) continue;
      let payload: unknown;
      try {
        payload = JSON.parse(dataLine);
      } catch {
        continue;
      }
      if (eventName === "snapshot" && payload && typeof payload === "object") {
        const notifications = (payload as { notifications?: ChatProcessNotification[] }).notifications;
        if (Array.isArray(notifications)) params.onSnapshot(notifications);
      } else if (eventName === "updated" && payload && typeof payload === "object") {
        const notification = (payload as { notification?: ChatProcessNotification }).notification;
        if (notification && typeof notification.conversationId === "string") {
          params.onUpdated(notification);
        }
      }
    }
  }
}
